import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { CircularProgress, Box, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresSubscription?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiresSubscription = true 
}) => {
  const { currentUser, loading: authLoading } = useFirebase();
  const { 
    isSubscriptionActive, 
    isLoading: subscriptionLoading
  } = useSubscription();
  const [checking, setChecking] = useState(true);
  const location = useLocation();
  
  // Verificar se já está na página de planos para evitar redirecionamentos em loop
  const isAlreadyOnPlanosPage = location.pathname === '/planos';

  useEffect(() => {
    // Simplificar a lógica para evitar loops infinitos
    if (!authLoading) {
      setChecking(false);
    }
  }, [authLoading]);

  if (authLoading || checking) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verificando acesso...
        </Typography>
      </Box>
    );
  }

  // Se não estiver autenticado, redireciona para o login
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Se requer assinatura, não está ativo, não está carregando e não está na página de planos,
  // redireciona para a página de planos
  if (requiresSubscription && !isSubscriptionActive && !subscriptionLoading && !isAlreadyOnPlanosPage) {
    return <Navigate to="/planos" state={{ from: location }} replace />;
  }

  // Se passou por todas as verificações, renderiza o componente filho
  return <>{children}</>;
};

export default ProtectedRoute;
